import { useState, useEffect, useCallback } from 'react';
import { API_URL } from '../constants';

/**
 * Manages session lifecycle: loading from sessionStorage, expiry/inactivity timers,
 * live countdown display, email sending, and session end.
 */
export function useSession(partnerId, sessionId) {
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sessionEnded, setSessionEnded] = useState(false);
  const [sessionEndReason, setSessionEndReason] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sessionCountdown, setSessionCountdown] = useState('');

  // Load session from sessionStorage on mount
  useEffect(() => {
    const storedSession = sessionStorage.getItem('instore_session');
    if (!storedSession) {
      setError('Session not found. Please scan the QR code again.');
      setLoading(false);
      return;
    }
    const sessionData = JSON.parse(storedSession);
    if (sessionData.session_id !== sessionId || sessionData.partner_id !== partnerId) {
      setError('Invalid session. Please scan the QR code again.');
      setLoading(false);
      return;
    }
    const expiresAt = new Date(sessionData.expires_at);
    if (new Date() > expiresAt) {
      sessionStorage.removeItem('instore_session');
      setError('Session has expired. Please scan the QR code again.');
      setLoading(false);
      return;
    }
    setSession(sessionData);
    setLoading(false);
  }, [partnerId, sessionId]);

  // Send results email helper (called internally by handleEndSession)
  const sendResultsEmail = useCallback(
    async (history, sizeHistory = {}) => {
      if (!session?.customer_email || !session?.session_token) return false;
      const results = history.filter((h) => h.type === 'tryon' || h.type === 'moment');
      if (results.length === 0) return false;
      setSendingEmail(true);
      try {
        const response = await fetch(
          `${API_URL}/api/public-studio/session/send-results-email?session_token=${session.session_token}`,
          {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ history: results, size_history: sizeHistory || {} }),
          }
        );
        const data = await response.json();
        if (data.success) {
          setEmailSent(true);
          return true;
        }
        return false;
      } catch (err) {
        console.error('Failed to send results email:', err);
        return false;
      } finally {
        setSendingEmail(false);
      }
    },
    [session]
  );

  // End session — accepts history and sizeHistory so email can be sent with full session results
  const handleEndSession = useCallback(
    async (reason = 'user_ended', history = [], sizeHistory = {}) => {
      if (session?.customer_email && history.some((h) => h.type === 'tryon' || h.type === 'moment')) {
        await sendResultsEmail(history, sizeHistory);
      }
      sessionStorage.removeItem('instore_session');
      setSessionEnded(true);
      setSessionEndReason(reason);
    },
    [session, sendResultsEmail]
  );

  // Session expiry check (every minute)
  useEffect(() => {
    if (!session) return;
    const checkExpiry = () => {
      const diff = new Date(session.expires_at) - new Date();
      if (diff <= 0) handleEndSession('expired', []);
    };
    checkExpiry();
    const interval = setInterval(checkExpiry, 60000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Inactivity auto-logout (30 minutes)
  useEffect(() => {
    if (!session) return;
    const INACTIVITY_TIMEOUT = 30 * 60 * 1000;
    let inactivityTimer;
    const resetTimer = () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      inactivityTimer = setTimeout(() => handleEndSession('inactivity', []), INACTIVITY_TIMEOUT);
    };
    const activityEvents = ['mousedown', 'mousemove', 'keydown', 'scroll', 'touchstart', 'click'];
    resetTimer();
    activityEvents.forEach((e) => document.addEventListener(e, resetTimer, { passive: true }));
    return () => {
      if (inactivityTimer) clearTimeout(inactivityTimer);
      activityEvents.forEach((e) => document.removeEventListener(e, resetTimer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // Live countdown display (updates every second)
  useEffect(() => {
    if (!session?.expires_at) return;
    const update = () => {
      const diff = new Date(session.expires_at) - new Date();
      if (diff <= 0) { setSessionCountdown(''); return; }
      const h = Math.floor(diff / 3600000);
      const m = Math.floor((diff % 3600000) / 60000);
      const s = Math.floor((diff % 60000) / 1000);
      if (h > 0) setSessionCountdown(`${h}h ${m}m`);
      else if (m > 0) setSessionCountdown(`${m}m ${String(s).padStart(2, '0')}s`);
      else setSessionCountdown(`${s}s`);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [session?.expires_at]);

  return {
    session,
    setSession,
    loading,
    error,
    sessionEnded,
    sessionEndReason,
    sessionCountdown,
    emailSent,
    sendingEmail,
    handleEndSession,
  };
}
